import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tag, Plus, Trash2, Loader2, Pause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { apiError } from "@/lib/format";

const empty = { code: "", percent: "10", valid_until: "", max_uses: "", notes: "" };
const fmt = (d) => (d ? d.split("-").reverse().join("/") : "Sem validade");

export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/admin/coupons").then((r) => setCoupons(r.data.coupons)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.code.trim()) return toast.error("Informe o código");
    const pct = parseFloat(form.percent);
    if (!(pct > 0 && pct <= 100)) return toast.error("Percentual entre 1 e 100");
    setBusy(true);
    try {
      await api.post("/admin/coupons", { code: form.code.trim(), percent: pct, valid_until: form.valid_until || null, max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null, notes: form.notes });
      toast.success("Cupom criado!"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const patch = async (code, body, msg) => {
    try { await api.patch(`/admin/coupons/${encodeURIComponent(code)}`, body); toast.success(msg); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (code) => {
    try { await api.delete(`/admin/coupons/${encodeURIComponent(code)}`); toast.success("Cupom excluído"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const statusOf = (c) => (!c.active ? ["Pausado", "bg-slate-500/15 text-slate-300"] : c.expired ? ["Expirado", "bg-red-500/15 text-red-300"]
    : c.max_uses && c.uses >= c.max_uses ? ["Esgotado", "bg-amber-500/15 text-amber-300"] : ["Ativo", "bg-emerald-500/15 text-emerald-300"]);

  return (
    <div className="space-y-6" data-testid="coupons-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-brand font-bold uppercase tracking-widest text-xs">Promoções</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase">Cupons de Desconto</h1>
          <p className="text-slate-400 text-sm mt-1">{coupons.filter((c) => c.active && !c.expired).length} ativos de {coupons.length}</p>
        </div>
        <Button data-testid="coupon-new-btn" onClick={() => setOpen(true)} className="bg-brand hover:bg-brand-hover font-bold uppercase tracking-wide"><Plus className="w-4 h-4 mr-1" /> Novo Cupom</Button>
      </div>

      <div className="rounded-2xl bg-surface border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-white/10">
            <th className="px-4 py-3">Código</th><th className="px-4 py-3">Desconto</th><th className="px-4 py-3">Validade</th><th className="px-4 py-3">Usos</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Obs.</th><th className="px-4 py-3 text-right">Ações</th></tr></thead>
          <tbody>
            {coupons.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">Nenhum cupom cadastrado.</td></tr>}
            {coupons.map((c) => { const [lbl, cls] = statusOf(c); return (
              <tr key={c.code} data-testid={`coupon-row-${c.code}`} className="border-b border-white/5">
                <td className="px-4 py-2 font-mono font-bold">{c.code}{c.courtesy && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 font-sans">CORTESIA</span>}</td>
                <td className="px-4 py-2 font-display text-lg font-black text-neon">{c.percent}%</td>
                <td className="px-4 py-2 text-xs">
                  <input data-testid={`coupon-valid-${c.code}`} type="date" defaultValue={c.valid_until || ""} key={c.valid_until || "none"}
                    onBlur={(e) => { const v = e.target.value; if (v !== (c.valid_until || "")) patch(c.code, v ? { valid_until: v } : { clear_valid_until: true }, "Validade atualizada"); }}
                    className="bg-ink border border-white/10 rounded px-2 py-1 text-xs text-white" />
                  <div className="text-slate-500 mt-0.5">{fmt(c.valid_until)}</div>
                </td>
                <td className="px-4 py-2 text-xs"><b className="text-white">{c.uses}</b>{c.max_uses ? ` / ${c.max_uses}` : ""} <span className="text-slate-500">({c.paid_uses} pagos)</span></td>
                <td className="px-4 py-2"><span data-testid={`coupon-status-${c.code}`} className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${cls}`}>{lbl}</span></td>
                <td className="px-4 py-2 text-xs text-slate-400">{c.notes || "—"}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button data-testid={`coupon-toggle-${c.code}`} onClick={() => patch(c.code, { active: !c.active }, c.active ? "Cupom pausado" : "Cupom reativado")} title={c.active ? "Pausar" : "Reativar"} className="text-slate-400 hover:text-white mr-3">{c.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}</button>
                  <button data-testid={`coupon-del-${c.code}`} onClick={() => remove(c.code)} disabled={c.uses > 0} title={c.uses > 0 ? "Já utilizado — pause em vez de excluir" : "Excluir"} className="text-slate-500 hover:text-red-400 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>); })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-md" data-testid="coupon-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase flex items-center gap-2"><Tag className="w-6 h-6 text-brand" /> Novo Cupom</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-slate-300">Código *</Label><Input data-testid="coupon-code" value={form.code} onChange={(e) => set("code", e.target.value.toLowerCase())} placeholder="ex: parceiro15" className="bg-ink border-white/10 mt-1 font-mono" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Desconto (%) *</Label><Input data-testid="coupon-percent" type="number" min="1" max="100" value={form.percent} onChange={(e) => set("percent", e.target.value)} className="bg-ink border-white/10 mt-1" /><p className="text-[11px] text-slate-500 mt-1">100% = inscrição cortesia</p></div>
              <div><Label className="text-slate-300">Válido até</Label><Input data-testid="coupon-valid-until" type="date" value={form.valid_until} onChange={(e) => set("valid_until", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
            </div>
            <div><Label className="text-slate-300">Limite de usos (opcional)</Label><Input data-testid="coupon-max-uses" type="number" min="1" value={form.max_uses} onChange={(e) => set("max_uses", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
            <div><Label className="text-slate-300">Observações</Label><Input data-testid="coupon-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
            <Button data-testid="coupon-submit-btn" onClick={submit} disabled={busy} className="w-full bg-brand hover:bg-brand-hover font-bold uppercase tracking-wide">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar Cupom"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
